import isNil from 'lodash/isNil';
import constants from '@/constants';
import startOfMonth from 'date-fns/startOfMonth';
import endOfMonth from 'date-fns/endOfMonth';
import format from 'date-fns/format';
import map from 'lodash/map';
import addDays from 'date-fns/addDays';
import isSameDay from 'date-fns/isSameDay';
import startOfWeek from 'date-fns/startOfWeek';
import filter from 'lodash/filter';
import endOfWeek from 'date-fns/endOfWeek';
import subMonths from 'date-fns/subMonths';
import subWeeks from 'date-fns/subWeeks';
import addMonths from 'date-fns/addMonths';
import addWeeks from 'date-fns/addWeeks';
import { mapGetters, mapMutations } from 'vuex';

export default {
  data() {
    return {
      schedulesLoaded: false,
      viewRange: {
        range: constants.WorkSchedule.ViewRange.WEEK,
        startDate: startOfMonth(new Date()),
        endDate: endOfMonth(new Date()),
      },
    };
  },

  computed: {
    ...mapGetters([
      'getWorkScheduleByExternalCode',
      'getAssignedWorkScheduleRanges',
      'getEmployeeSchedules',
    ]),

    isWeekView() {
      return this.viewRange.range === constants.WorkSchedule.ViewRange.WEEK;
    },

    resetButtonText() {
      switch (this.viewRange.range) {
        case constants.WorkSchedule.ViewRange.WEEK:
          return this.$t('range.currentWeek');
        case constants.WorkSchedule.ViewRange.MONTH:
          return this.$t('range.currentMonth');
        default:
          return this.$t('range.today');
      }
    },

    currentTimeRange() {
      switch (this.viewRange.range) {
        case constants.WorkSchedule.ViewRange.WEEK: {
          const startingDate = this.$d(this.viewRange.startDate, 'rangeDateFormat');

          const endingDate = this.$d(this.viewRange.endDate, 'rangeDateFormat');

          return `${startingDate} - ${endingDate}`;
        }
        case constants.WorkSchedule.ViewRange.MONTH: {
          return this.$d(this.viewRange.startDate, 'rangeMonthFormat');
        }
        default:
          return '-';
      }
    },

    previousTimeRangeTooltip() {
      return `${this.$t('range.seePrev')} ${this.getViewRangeReadableText(this.viewRange.range)}`;
    },

    nextTimeRangeTooltip() {
      return `${this.$t('range.seeNext')} ${this.getViewRangeReadableText(this.viewRange.range)}`;
    },

    viewRanges() {
      return map(
        Object.values(constants.WorkSchedule.ViewRange),
        (viewRange) => ({
          text: this.getViewRangeReadableText(viewRange),
          value: viewRange,
        }),
      );
    },

    viewRangeTicks() {
      switch (this.viewRange.range) {
        case constants.WorkSchedule.ViewRange.WEEK:
        case constants.WorkSchedule.ViewRange.MONTH: {
          const weekDayFormat = 'EEE';
          const dayOfMonthFormat = 'd';

          let dayIterator = new Date(this.viewRange.startDate);

          const ticks = [];

          do {
            ticks.push({
              upperText: format(dayIterator, weekDayFormat),
              lowerText: format(dayIterator, dayOfMonthFormat),
            });

            dayIterator = addDays(dayIterator, 1);
          } while (!isSameDay(dayIterator, addDays(this.viewRange.endDate, 1)));

          return ticks;
        }
        default: return [];
      }
    },
  },

  methods: {
    ...mapMutations(['setDailyWorkScheduleByEmployee']),

    getViewRangeReadableText(viewRange) {
      switch (viewRange) {
        case constants.WorkSchedule.ViewRange.WEEK:
          return this.$t('range.week');
        case constants.WorkSchedule.ViewRange.MONTH:
          return this.$t('range.month');
        default:
          return this.$t('misc.unknown');
      }
    },

    handleViewRangeChange() {
      this.viewRange.startDate = this.isWeekView
        ? startOfWeek(startOfMonth(this.viewRange.startDate), { weekStartsOn: 0 })
        : startOfMonth(this.viewRange.endDate);

      this.viewRange.endDate = this.getViewRangeEndDate();

      this.fetchAssignedWorkScheduleRanges();
    },

    handleTimeRangePreviousPage() {
      this.resetAllBars();
      this.viewRange.startDate = this.isWeekView
        ? subWeeks(this.viewRange.startDate, 1)
        : subMonths(this.viewRange.startDate, 1);

      this.viewRange.endDate = this.getViewRangeEndDate();

      this.fetchAssignedWorkScheduleRanges();
    },

    handleTimeRangeNextPage() {
      this.resetAllBars();
      this.viewRange.startDate = this.isWeekView
        ? addWeeks(this.viewRange.startDate, 1)
        : addMonths(this.viewRange.startDate, 1);

      this.viewRange.endDate = this.getViewRangeEndDate();

      this.fetchAssignedWorkScheduleRanges();
    },

    resetViewRangeDates() {
      this.resetAllBars();
      this.viewRange.startDate = this.isWeekView
        ? this.getStartOfCurrentWeek()
        : startOfMonth(new Date());

      this.viewRange.endDate = this.getViewRangeEndDate();

      this.fetchAssignedWorkScheduleRanges();
    },

    getViewRangeEndDate() {
      return this.isWeekView
        ? endOfWeek(this.viewRange.startDate)
        : endOfMonth(this.viewRange.startDate);
    },

    setDailyScheduleForEmployees(assignments) {
      const schedules = {};
      assignments.forEach(
        // eslint-disable-next-line consistent-return
        (assignment) => {
          const workScheduleRanges = this.getAssignedWorkScheduleRanges(assignment);
          let daySegments = [];
          // eslint-disable-next-line consistent-return
          workScheduleRanges.forEach((range) => {
            let currentDay;
            if (range.type !== constants.WorkSchedule.ABSENCE) {
              currentDay = range.workScheduleDayNumberFromDate;
            }
            daySegments = daySegments.concat(this.getDaySegments(range, currentDay));
          });
          schedules[assignment] = daySegments;
          daySegments = [];
        },
      );
      this.setDailyWorkScheduleByEmployee(schedules);
      return schedules;
    },

    setDailyWorkScheduleForEmployee(assignment) {
      const weekSchedules = {};
      const workScheduleRanges = this.getEmployeeSchedules()[0];
      let daySegments = [];
      // eslint-disable-next-line consistent-return
      workScheduleRanges.assignedWorkScheduleRangeSummaries.forEach((range) => {
        let currentDay;
        if (range.type !== constants.WorkSchedule.ABSENCE) {
          currentDay = range.startingWorkScheduleDay;
        }
        daySegments = daySegments.concat(this.getDaySegments(range, currentDay));
      });
      weekSchedules[assignment] = daySegments;
      this.setDailyWorkScheduleByEmployee(weekSchedules);
      return weekSchedules;
    },

    getDaySegments(range, startingDay) {
      const fullSchedule = range.assignedWorkSchedule;
      const daySegments = [];
      const differenceInDays = this.getDifferenceInDays(range.startDate, range.endDate);

      let currentDay = startingDay;
      let count = 0;

      while (count < differenceInDays) {
        const date = new Date(range.startDate);
        date.setDate(date.getDate() + count);

        // eslint-disable-next-line no-loop-func
        const day = fullSchedule ? fullSchedule.days.find((it) => it.day === currentDay) : null;

        if (day) {
          daySegments.push({
            startTime: day.segments[0] ? day.segments[0].startTime : null,
            endTime: day.segments[day.segments.length - 1]
              ? day.segments[day.segments.length - 1].endTime : null,
            isOffDay: range.isOffDay,
            externalCode: range.externalCode,
            type: range.type,
            minuteRange: constants.WorkSchedule.MINUTES_IN_DAY,
            date,
          });
        } else {
          daySegments.push({
            startTime: null,
            endTime: null,
            isOffDay: range.isOffDay,
            externalCode: range.externalCode,
            type: range.type,
            minuteRange: constants.WorkSchedule.MINUTES_IN_DAY,
            date,
          });
        }

        if (currentDay === 7) {
          currentDay = 1;
        } else {
          currentDay++;
        }

        count++;
      }

      return daySegments;
    },

    getStartOfCurrentWeek() {
      return startOfWeek(new Date(), { weekStartsOn: 0 });
    },

    getBarHeight() {
      if (this.isMobile()) {
        return '180px';
      }
      return '90px';
    },

    getBarMinWidth() {
      return this.viewRange.range === constants.WorkSchedule.ViewRange.WEEK ? '400px' : '1200px';
    },

    getBarRef(assignmentId) {
      return `${assignmentId}_ws_bar`;
    },

    getWorkScheduleCode(workScheduleRange) {
      return workScheduleRange.externalCode == null
        ? constants.NONE
        : workScheduleRange.externalCode;
    },

    scrollAllBars(event, assignmentId) {
      const barsToScroll = map(
        filter(Object.keys(this.$refs), (key) => key.includes('ws_bar')
          && (isNil(assignmentId) || key !== this.getBarRef(assignmentId))),
        (key) => {
          const bar = this.$refs[key];
          return Array.isArray(bar) ? bar[0] : bar;
        },
      );
      barsToScroll.forEach((bar) => {
        bar.scrollLeft = event.target.scrollLeft;
      });
    },

    resetAllBars() {
      const barsToScroll = map(
        filter(Object.keys(this.$refs), (key) => key.includes('ws_bar')),
        (key) => {
          const bar = this.$refs[key];
          return Array.isArray(bar) ? bar[0] : bar;
        },
      );
      barsToScroll.forEach((bar) => {
        bar.scrollLeft = 0;
      });
    },

    getDifferenceInDays(start, end) {
      const differenceInTime = (new Date(end)).getTime()
        - (new Date(start)).getTime();
      return differenceInTime / (1000 * 3600 * 24) + 1;
    },
  },
};
